import { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import { IntakeContext } from '../context/IntakeContext'
// import { Link } from "react-router-dom";
import { selectData } from 'layouts/onboarding'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { useFieldFocus } from 'context/FieldFocusContext'

// @mui material components
import HelpIcon from '@mui/icons-material/Help'
import { IconButton, Tooltip, Fade, Grid, Typography, Box } from '@mui/material'

import { UpdatePopup } from 'layouts/onboarding/UpdatePopup'

const StyledMSB = styled.div`
  text-align: center;
  position: relative;
  padding: 16px;
  height: 100%;
  cursor: pointer;
  border: ${(props) =>
    props.active ? '2px solid #a3a7aa' : '2px solid #e5e5e5'};
  background: ${(props) => (props.active ? '#eeeeee' : '#fff')};
  color: #212121;
  border-radius: 0.75rem;
  transition: 0.2s all ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    border: 2px solid #a3a7aa;
    background: #f5f5f5;
  }

  > h6,
  h5 {
    color: #212121;
    letter-spacing: 0.3px;
    font-weight: 600;
    line-height: 1.4;
    margin: 0;
  }

  > span {
    border-radius: 50%;
    height: 1.5em;
    width: 1.5em;
    padding: 8px;
    display: flex;
    justify-content: center;
    align-items: center;

    &.yesIcon {
      color: #4caf50;
      background-color: #dbefdc;
    }
    &.noIcon {
      color: #fd5c70;
      background-color: #fedee2;
    }
  }
`
const StyledAgencyLogo = styled.img`
  max-height: 60px;
  width: auto;
  max-width: 100%;
`

const OnboardingMilitaryBranch = ({
  id,
  preHeader,
  header,
  subHeader,
  helpText,
}) => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)

  const [tileValue, setTileValue] = useState(intakeState?.[id])

  const toggleTileValue = (name) => {
    setTileValue(name)
  }

  useEffect(() => {
    if (typeof tileValue != 'string') return
    updateIntakeStateAtKey({
      [id]: tileValue,
    })
  }, [tileValue])

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992)
  const fieldId = 'militaryHelp'
  const newHelpText = 'If you were in the reserves this also qualifies'

  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  useEffect(() => {
    registerHelpField(fieldId, newHelpText)
  }, [fieldId])

  return (
    <Box
      sx={{ textAlign: 'left' }}
      onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
      onMouseLeave={() => handleBlur()}
    >
      <Box>
        <Typography variant="subtitle1" fontSize={{ xs: '16px', sm: '20px' }}>
          {preHeader || ''}
        </Typography>
      </Box>
      <Box mb={1}>
        <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
          {header || "Let's start with your basic information:"}
          {isMobile && (
            <Tooltip
              TransitionComponent={Fade}
              title={
                helpText || 'This helps us calculate your retirement benefits'
              }
              placement="top"
              color="info"
              enterTouchDelay={0}
              leaveTouchDelay={3000}
            >
              <IconButton size="small" color="dark">
                <HelpIcon sx={{ marginTop: '-10px' }} />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
      </Box>
      <Typography
        variant="body1"
        sx={{ lineHeight: '1.2' }}
        fontSize={{ xs: '16px', sm: '20px' }}
      >
        {subHeader ||
          'Let us know your name and other basic details about you!'}
      </Typography>
      <Box my={4}>
        <Grid container spacing={1} justifyContent="center" mb={3}>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={tileValue === 'unitedstatesarmy'}
              onClick={() => toggleTileValue('unitedstatesarmy')}
            >
              <StyledAgencyLogo src="https://upload.wikimedia.org/wikipedia/commons/8/8d/Logo_of_the_United_States_Army.svg" />
              <Typography variant="h6" textTransform="capitalize">
                Army
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={tileValue === 'unitedstatesnavy'}
              onClick={() => toggleTileValue('unitedstatesnavy')}
            >
              <StyledAgencyLogo src="https://upload.wikimedia.org/wikipedia/commons/b/b1/Emblem_of_the_United_States_Navy.svg" />
              <Typography variant="h6" textTransform="capitalize">
                Navy
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={tileValue === 'unitedstatesmarinecorps'}
              onClick={() => toggleTileValue('unitedstatesmarinecorps')}
            >
              <StyledAgencyLogo src="https://upload.wikimedia.org/wikipedia/commons/9/90/Seal_of_the_US_Marine_Corps.svg" />
              <Typography variant="h6" textTransform="capitalize">
                Marine Corps
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={tileValue === 'unitedstatesairforce'}
              onClick={() => toggleTileValue('unitedstatesairforce')}
            >
              <StyledAgencyLogo src="https://upload.wikimedia.org/wikipedia/commons/9/98/US_Air_Force_Logo_-_Black_and_White_Version.svg" />
              <Typography variant="h6" textTransform="capitalize">
                Air Force
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={tileValue === 'unitedstatescoastguard'}
              onClick={() => toggleTileValue('unitedstatescoastguard')}
            >
              <StyledAgencyLogo src="https://upload.wikimedia.org/wikipedia/commons/e/e5/Mark_of_the_U.S._Coast_Guard.svg" />
              <Typography variant="h6" textTransform="capitalize">
                Coast Guard
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6} sm={4}>
            <StyledMSB
              active={tileValue === 'unitedstatesspaceforce'}
              onClick={() => toggleTileValue('unitedstatesspaceforce')}
            >
              <StyledAgencyLogo src="https://upload.wikimedia.org/wikipedia/commons/archive/5/5a/20200723011556%21Logo_of_the_United_States_Space_Force.png" />
              <Typography variant="h6" textTransform="capitalize">
                Space Force
              </Typography>
            </StyledMSB>
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}

export default OnboardingMilitaryBranch
